import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  AlertCircle,
  Calendar,
  User,
  FileText,
  X,
  CheckCircle2,
  Clock,
  TrendingUp,
  Building,
  MapPin,
  Tag,
  ExternalLink,
  ArrowLeft,
  Plus,
  XCircle
} from 'lucide-react';
import moment from 'moment';

const FlagDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [flag, setFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [investigators, setInvestigators] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [creatingCase, setCreatingCase] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    fetchFlag();
    fetchInvestigators();
    fetchFacilities();
  }, [id]);

  const fetchFlag = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/flags/${id}`);
      setFlag(response.data.data);
    } catch (error) {
      console.error('Error fetching flag:', error);
      toast.error('Failed to load flag');
      navigate('/flags');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestigators = async () => {
    try {
      const response = await api.get('/users/investigators');
      setInvestigators(response.data.data || []);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to load facilities');
    }
  };

  const handleFacilityChange = (e) => {
    const selectedFacilityId = e.target.value;
    if (selectedFacilityId) {
      const selectedFacility = facilities.find(f => f.id.toString() === selectedFacilityId);
      if (selectedFacility) {
        formik.setFieldValue('facility', {
          name: selectedFacility.name,
          id: selectedFacility.id.toString(),
          location: selectedFacility.address || '',
        });
      }
    } else {
      formik.setFieldValue('facility', {
        name: '',
        id: '',
        location: '',
      });
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    caseType: Yup.string().required('Case type is required'),
    priority: Yup.string().required('Priority is required'),
  });

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      caseType: '',
      priority: 'High',
      assignedTo: '',
      facility: {
        name: '',
        id: '',
        location: '',
      },
      tags: [],
      teamMembers: [],
      isAdminViewOnly: false,
      dueDate: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setCreatingCase(true);
        const cleanedValues = {
          title: values.title,
          description: values.description || undefined,
          caseType: values.caseType,
          priority: values.priority,
          assignedTo: values.assignedTo || undefined,
          tags: values.tags && values.tags.length > 0 ? values.tags : undefined,
        };

        if (values.facility && values.facility.name && values.facility.name.trim() !== '') {
          cleanedValues.facility = values.facility;
        }

        if (values.dueDate) {
          cleanedValues.dueDate = values.dueDate;
        }

        if (values.teamMembers && values.teamMembers.length > 0) {
          cleanedValues.teamMembers = values.teamMembers;
        }

        if (values.isAdminViewOnly) {
          cleanedValues.isAdminViewOnly = values.isAdminViewOnly;
        }

        Object.keys(cleanedValues).forEach(key => {
          if (cleanedValues[key] === undefined) {
            delete cleanedValues[key];
          }
        });

        const response = await api.post(`/flags/${id}/create-case`, cleanedValues);
        toast.success('Case created successfully from flag');
        setShowCreateCaseModal(false);
        const caseId = response.data.data.case._id || response.data.data.case.id;
        navigate(`/cases/${caseId}`);
        fetchFlag(); // Refresh flag to show updated status
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to create case');
      } finally {
        setCreatingCase(false);
      }
    },
  });

  // Update formik values when flag loads
  useEffect(() => {
    if (flag) {
      formik.setValues({
        title: flag.title || '',
        description: flag.description || '',
        caseType: '',
        priority: flag.priority || 'High',
        assignedTo: flag.assignedTo?.id || flag.assignedTo?._id || '',
        facility: flag.facility || {
          name: flag.facilityName || '',
          id: flag.facilityId || '',
          location: flag.facilityLocation || '',
        },
        tags: [],
        teamMembers: [],
        isAdminViewOnly: false,
        dueDate: '',
      });
    }
  }, [flag]);

  const handleReject = async () => {
    try {
      setRejecting(true);
      await api.put(`/flags/${id}`, {
        status: 'Dismissed'
      });
      toast.success('Flag rejected and dismissed');
      setShowRejectModal(false);
      fetchFlag();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject flag');
    } finally {
      setRejecting(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusColor = (status) => {
    const colors = {
      Open: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Under Review': 'bg-blue-100 text-blue-800 border-blue-300',
      'Case Created': 'bg-green-100 text-green-800 border-green-300',
      Dismissed: 'bg-gray-100 text-gray-800 border-gray-300',
      Resolved: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Urgent: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!flag) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Flag not found</p>
        <Link to="/flags" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
          Back to Flags
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/flags"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{flag.flagNumber}</h1>
            <p className="text-gray-600 mt-1">{flag.title}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {flag.status === 'Open' && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                <span>Reject Flag</span>
              </button>
              <button
                onClick={() => setShowCreateCaseModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create Case</span>
              </button>
            </>
          )}
          {flag.caseId && (
            <Link
              to={`/cases/${flag.caseId.id || flag.caseId._id || (typeof flag.caseId === 'string' ? flag.caseId : null)}`}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              <span>View Case</span>
              <ExternalLink className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Flag Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <div className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(flag.status)}`}>
                {flag.status}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Severity</label>
            <div className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(flag.severity)}`}>
                {flag.severity}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Priority</label>
            <div className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(flag.priority)}`}>
                {flag.priority}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Source</label>
            <p className="mt-1 text-sm text-gray-900">{flag.source}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Created</label>
            <p className="mt-1 text-sm text-gray-900">
              {moment(flag.createdAt).format('MMM DD, YYYY hh:mm A')}
            </p>
          </div>
          {flag.assignedTo && (
            <div>
              <label className="text-sm font-medium text-gray-500">Assigned To</label>
              <p className="mt-1 text-sm text-gray-900">
                {flag.assignedTo.firstName} {flag.assignedTo.lastName}
              </p>
            </div>
          )}
        </div>

        {flag.facility && (
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Building className="w-4 h-4 mr-2" />
              Facility Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {flag.facility.name && (
                <div>
                  <label className="text-xs text-gray-500">Facility Name</label>
                  <p className="text-sm text-gray-900 mt-1">{flag.facility.name}</p>
                </div>
              )}
              {flag.facility.id && (
                <div>
                  <label className="text-xs text-gray-500">Facility ID</label>
                  <p className="text-sm text-gray-900 mt-1">{flag.facility.id}</p>
                </div>
              )}
              {flag.facility.location && (
                <div>
                  <label className="text-xs text-gray-500">Location</label>
                  <p className="text-sm text-gray-900 mt-1">{flag.facility.location}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Description</h3>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{flag.description || 'No description provided'}</p>
        </div>

        {flag.triggerData && Object.keys(flag.triggerData).length > 0 && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Trigger Data</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {JSON.stringify(flag.triggerData, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {flag.tags && flag.tags.length > 0 && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Tag className="w-4 h-4 mr-2" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {flag.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCreateCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Create Case from Flag</h2>
              <button
                onClick={() => setShowCreateCaseModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Creating case from:</strong> {flag.flagNumber} - {flag.title}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  The flag information will be linked to this case automatically.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter case title"
                />
                {formik.touched.title && formik.errors.title && (
                  <p className="mt-1 text-sm text-red-600">{formik.errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter case description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Case Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="caseType"
                    value={formik.values.caseType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select case type</option>
                    <option value="Stolen Pins">Stolen Pins</option>
                    <option value="Suspicious Payments">Suspicious Payments</option>
                    <option value="Contraband">Contraband</option>
                    <option value="Escape">Escape</option>
                    <option value="Chargebacks">Chargebacks</option>
                    <option value="Staff (restricted)">Staff (restricted)</option>
                    <option value="Other">Other</option>
                  </select>
                  {formik.touched.caseType && formik.errors.caseType && (
                    <p className="mt-1 text-sm text-red-600">{formik.errors.caseType}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="priority"
                    value={formik.values.priority}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Urgent">Urgent</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                  {formik.touched.priority && formik.errors.priority && (
                    <p className="mt-1 text-sm text-red-600">{formik.errors.priority}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign To
                </label>
                <select
                  name="assignedTo"
                  value={formik.values.assignedTo}
                  onChange={formik.handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select investigator</option>
                  {investigators.map((investigator) => (
                    <option key={investigator.id || investigator._id} value={investigator.id || investigator._id}>
                      {investigator.firstName} {investigator.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Members (Optional)
                </label>
                <select
                  multiple
                  name="teamMembers"
                  value={formik.values.teamMembers || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    formik.setFieldValue('teamMembers', selected);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px]"
                >
                  {investigators.map((investigator) => (
                    <option key={investigator.id || investigator._id} value={investigator.id || investigator._id}>
                      {investigator.firstName} {investigator.lastName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple team members</p>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isAdminViewOnly"
                    checked={formik.values.isAdminViewOnly}
                    onChange={formik.handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Admin View Only</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">Only Administrators and Supervisors can view this case</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formik.values.dueDate || ''}
                  onChange={formik.handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="mt-1 text-xs text-gray-500">
                  A To-Do will be automatically created for the assigned investigator when a due date is set
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facility Name
                  </label>
                  <select
                    name="facility.name"
                    value={formik.values.facility.id || ''}
                    onChange={handleFacilityChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select facility</option>
                    {facilities.map((facility) => (
                      <option key={facility.id} value={facility.id}>
                        {facility.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facility ID
                  </label>
                  <input
                    type="text"
                    name="facility.id"
                    value={formik.values.facility.id || ''}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Facility ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="facility.location"
                    value={formik.values.facility.location || ''}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Location"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateCaseModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingCase}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creatingCase ? 'Creating...' : 'Create Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Flag Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Reject Flag</h2>
                  <p className="text-sm text-gray-600">Are you sure you want to reject this flag?</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>Flag:</strong> {flag.flagNumber}
                </p>
                <p className="text-sm text-yellow-800 mt-1">
                  <strong>Title:</strong> {flag.title}
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  Rejecting this flag will mark it as "Dismissed" and it will no longer appear in open flags.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {rejecting ? 'Rejecting...' : 'Reject Flag'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlagDetail;
